import { useState } from "react";
import { useAuth } from "@/contexts/AuthContext";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { fmtDate } from "@/lib/dateFormat";

const fmt = (n) => new Intl.NumberFormat('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(n || 0);

export default function MigrateInvoiceDatesPage() {
  const { user } = useAuth();
  const isAdmin = (user?.roles || []).includes("Admin") || (user?.roles || []).includes("admin");
  const [dryRunResult, setDryRunResult] = useState(null);
  const [writeResult, setWriteResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [writing, setWriting] = useState(false);

  if (!isAdmin) {
    return <div className="p-8 text-center"><p className="text-zinc-400">Admin access required.</p></div>;
  }

  const runDryRun = async () => {
    setLoading(true); setDryRunResult(null); setWriteResult(null);
    try {
      const { data } = await api.post("/admin/migrate-invoice-dates", { dry_run: true });
      setDryRunResult(data);
      toast.success(`Dry-run complete: ${data.total_migrated} orders would be migrated`);
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setLoading(false); }
  };

  const executeWrite = async () => {
    if (!window.confirm(`This will update invoice_date on ${dryRunResult?.total_migrated || 0} orders. This action writes to the production database. Confirm?`)) return;
    setWriting(true);
    try {
      const { data } = await api.post("/admin/migrate-invoice-dates", { dry_run: false });
      setWriteResult(data);
      toast.success(`Migration complete: ${data.total_migrated} orders updated`);
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setWriting(false); }
  };

  const result = writeResult || dryRunResult;

  return (
    <div data-testid="migrate-invoice-dates-page">
      <h1 className="font-heading text-3xl font-bold tracking-tight text-zinc-950 mb-2">Invoice Date Migration</h1>
      <div className="border border-blue-200 bg-blue-50 p-4 rounded-sm mb-6 text-sm text-blue-900">
        <p className="font-semibold mb-1">What does this migration do?</p>
        <p>ORBIT's PnL Audit Report uses a top-level <code className="bg-blue-100 px-1 rounded">invoice_date</code> field on each order to determine which month the order belongs to for profit & loss calculations.</p>
        <p className="mt-2">Older orders (created before this feature) don't have this field yet. This migration copies the invoice date from the order's invoice data or — if that's not available — uses the date the invoice was uploaded as a fallback.</p>
        <p className="mt-2 font-semibold">Step 1: Click "Run Dry-Run" to see how many orders would be affected (no data is changed).</p>
        <p>Step 2: Review the list below. If it looks correct, click "Execute Migration" to write the data.</p>
      </div>

      <div className="flex gap-3 mb-6">
        <Button onClick={runDryRun} disabled={loading} className="rounded-sm h-9 text-xs" data-testid="dry-run-btn">
          {loading ? "Counting..." : "Run Dry-Run (Preview Counts Only)"}
        </Button>
        <Button onClick={executeWrite} disabled={!dryRunResult || writing || !!writeResult}
          className={`rounded-sm h-9 text-xs ${dryRunResult && !writeResult ? 'bg-green-600 hover:bg-green-700' : ''}`} data-testid="execute-btn">
          {writing ? "Writing..." : writeResult ? "Migration Complete" : "Execute Migration (Write Data)"}
        </Button>
      </div>

      {/* Counts */}
      {result && (
        <div className="grid grid-cols-3 gap-4 mb-6" data-testid="migration-counts">
          <div className="border border-zinc-200 p-4 rounded-sm text-center">
            <p className="text-[10px] text-zinc-400 uppercase">Source A: From Invoice Data</p>
            <p className="text-3xl font-mono font-bold text-green-700">{result.count_a_from_invoice_data}</p>
            <p className="text-[10px] text-zinc-500">Orders with explicit invoice date in their records</p>
          </div>
          <div className="border border-zinc-200 p-4 rounded-sm text-center">
            <p className="text-[10px] text-zinc-400 uppercase">Source B: From Upload Timestamp</p>
            <p className="text-3xl font-mono font-bold text-amber-600">{result.count_b_from_workflow_fallback}</p>
            <p className="text-[10px] text-zinc-500">Orders using the invoice upload date as fallback</p>
          </div>
          <div className="border border-zinc-200 p-4 rounded-sm text-center">
            <p className="text-[10px] text-zinc-400 uppercase">Not Yet Invoiced</p>
            <p className="text-3xl font-mono font-bold text-zinc-400">{result.count_c_not_yet_invoiced}</p>
            <p className="text-[10px] text-zinc-500">Orders with no invoice data — skipped</p>
          </div>
        </div>
      )}

      {/* Status banner */}
      {result && (
        <div className={`p-3 rounded-sm mb-4 text-sm font-medium ${result.dry_run ? 'bg-amber-50 border border-amber-200 text-amber-800' : 'bg-green-50 border border-green-200 text-green-800'}`}>
          {result.dry_run
            ? `Dry-run complete. ${result.total_migrated} orders would be migrated. No data was changed. Review the list below and click "Execute Migration" when ready.`
            : `Migration executed successfully. ${result.total_migrated} orders updated with invoice_date. Audit log entries created for each order.`
          }
        </div>
      )}

      {/* Orders table */}
      {result && (result.migrated_orders || []).length > 0 && (
        <div className="border border-zinc-200 overflow-x-auto rounded-sm" data-testid="migration-orders-table">
          <table className="data-table w-full">
            <thead><tr><th>#</th><th>Order #</th><th>Customer</th><th>Migrated Invoice Date</th><th>Source</th><th className="num">Order Value (INR)</th></tr></thead>
            <tbody>
              {result.migrated_orders.map((o, i) => (
                <tr key={i} className={o.invoice_date_source === "migrated_from_workflow_fallback" ? "bg-amber-50" : ""}>
                  <td className="text-[10px] text-zinc-400">{i + 1}</td>
                  <td className="font-mono text-xs font-semibold">{o.order_no}</td>
                  <td className="text-xs">{o.customer_name}</td>
                  <td className="font-mono text-xs">{fmtDate(o.invoice_date)}</td>
                  <td className="text-[10px]">
                    {o.invoice_date_source === "migrated_from_invoice_data"
                      ? <span className="px-1.5 py-0.5 bg-green-100 text-green-700 rounded-sm text-[9px] font-semibold">From Invoice Data</span>
                      : <span className="px-1.5 py-0.5 bg-amber-100 text-amber-700 rounded-sm text-[9px] font-semibold">Upload Timestamp Fallback</span>
                    }
                  </td>
                  <td className="num font-mono text-xs">{fmt(o.order_value)}</td>
                </tr>
              ))}
            </tbody>
          </table>
          <div className="p-2 bg-zinc-50 text-[10px] text-zinc-500 border-t border-zinc-200">
            Showing {result.migrated_orders.length} orders. Amber-highlighted rows use upload timestamp as fallback — verify these manually against invoice PDFs if needed.
          </div>
        </div>
      )}
    </div>
  );
}
